import { useEffect, useState } from 'react';
import classnames from 'classnames';
import dynamic from 'next/dynamic';
import { connect } from 'react-redux';
import { Category, isShippableCategory } from '@sbt-web/category-utils';
import { useViewport } from '@sbt-web/hooks';
import { searchItems } from '@sbt-web/network/client';
import { AdTypes, SearchResult, GeoEntry, SeoMetaPayload } from '@sbt-web/network/types';
import { AdItemCardResponsiveSkeleton, isCategoryForLargeCard } from '@sbt-web/item-card';
import { TuttaItalia } from '@sbt-web/utils';
import { AdsBeforeListingBlock } from '@/client/components/Adv/AdSense/listing/AdsBeforeListingBlock';
import { useAdvToggle } from '@/client/hooks/useAdvToggle';
import type { FiltersConfigState } from '@/reducers/filtersConfig';
import type { RootState } from '@/reducers/index';
import { mapSearchStateToSearchParams } from '@/reducers/items';
import { SearchState } from '@/reducers/search';
import type { DecoratedItem } from '@/shared/models';
import { sendNotification } from '@/tools/Notifications';
import { ListingHeading } from '../../SEO/ListingHeading/ListingHeading';
import ZrpExtendedSearch from '../Elements/ZrpExtendedSearch';
import { ListingItem } from './ListingItem';
import ownClasses from './ItemListContainer.module.scss';
import adItemClasses from './AdItem.module.scss';
const ZeroResults = dynamic(() => import('../Elements/ZeroResults').then(m => m.ZeroResults));
const NationwideSearchExpander = dynamic(() => import('../../Search/Elements/SearchNationwide'), {
  ssr: false
});
interface StateProps {
  items: DecoratedItem[];
  loading: boolean;
  hasError: boolean;
  listingCategory: Category;
  adType: AdTypes;
  query: string;
  geo: GeoEntry;
  filters: Record<string, string>;
  filtersLabels?: Record<string, string>;
  search: SearchState;
  filtersConfig: FiltersConfigState;
  environmentId?: string;
}
interface OwnProps {
  seo?: SeoMetaPayload;
  macroCategory: string;
  handleItemClick: () => void;
}
type Props = OwnProps & StateProps;
const itemSkeletonsCount = 10;
const ZRP_EXPANDED_SEARCH_ITEMS_COUNT = 35;
function extendSearch(search: SearchState): SearchState {
  return {
    ...search,
    geo: {
      region: TuttaItalia
    },
    includeShippableOnly: true,
    page: 1
  };
}
function buildExtSearchParams(search: SearchState, filtersConfig: FiltersConfigState) {
  const params = mapSearchStateToSearchParams(search, filtersConfig);
  params.lim = ZRP_EXPANDED_SEARCH_ITEMS_COUNT;
  return params;
}
export function ItemListContainer({
  filters,
  filtersLabels,
  geo,
  hasError,
  items,
  listingCategory,
  adType,
  loading,
  query,
  seo,
  macroCategory,
  handleItemClick,
  search,
  filtersConfig,
  environmentId
}: Props) {
  const {
    isTablet,
    isDesktop
  } = useViewport();
  const {
    noAdvToggle,
    optimizelyReady
  } = useAdvToggle();
  useEffect(() => {
    if (hasError) {
      sendNotification('Si è verificato un problema', 'alert');
    }
  }, [hasError]);
  const [extSearchData, setExtSearchData] = useState<SearchResult>();
  const [extendedSearch, setExtendedSearch] = useState<SearchState>();
  const showZRP = items.length === 0;
  const hasExtendedSearch = !!extendedSearch && !!extSearchData?.ads.length;
  useEffect(() => {
    if (!showZRP) return;
    if (!isShippableCategory(listingCategory.id)) return;
    let isMounted = true;
    const applyExtendedSearch = async () => {
      try {
        const extendedSearch_0 = extendSearch(search);
        const params = buildExtSearchParams(extendedSearch_0, filtersConfig);
        const {
          body: data
        } = await searchItems(process.env.NEXT_PUBLIC_HADES_BASE_URL, params, environmentId ?? undefined, true);
        if (!isMounted) return;
        setExtSearchData(data);
        setExtendedSearch(extendedSearch_0);
      } catch {
        if (!isMounted) return;
        setExtSearchData(undefined);
        setExtendedSearch(undefined);
      }
    };
    applyExtendedSearch();
    return () => {
      isMounted = false;
    };
  }, [showZRP, listingCategory.id, search, filtersConfig, environmentId]);
  const useLargeCard = isCategoryForLargeCard(listingCategory.id);
  return <div className={classnames(ownClasses.container)}>
      <div data-google-interstitial="false">
        {!showZRP && <ListingHeading seo={seo} />}
        {!showZRP && optimizelyReady && noAdvToggle !== 'B' ? <AdsBeforeListingBlock /> : null}
        {isDesktop || isTablet ? <div className={classnames('search-expansion-container', ownClasses['search-expansion-container'], {
        [ownClasses['search-expansion-container--zero-results']]: items.length === 0
      })}>
            <NationwideSearchExpander id="desktop-listing-nationwide-search-expander" />
          </div> : null}
      </div>

      {loading ? <ul className={classnames('skeletons', ownClasses.list)}>
          {Array.from({
        length: itemSkeletonsCount
      }).map((_, i) => <li key={i * 3} className={classnames('items__item', adItemClasses.adItemCard, {
        [adItemClasses.large]: useLargeCard
      })}>
              <AdItemCardResponsiveSkeleton variant={useLargeCard ? 'large' : 'small'} />
            </li>)}
        </ul> : <div className={ownClasses.list}>
          <div>
            {items.map((item, index) => <ListingItem key={item.item.urn} item={item} index={index} listingCategory={listingCategory.id} adType={adType} filters={filters} filtersLabels={filtersLabels} query={query} geo={geo} macroCategory={macroCategory} handleItemClick={handleItemClick} filterStore={filtersConfig.filtersStore} />)}
          </div>
        </div>}

      <div data-google-interstitial="false">
        {showZRP && <>
            {hasExtendedSearch ? <ZrpExtendedSearch extendedSearch={extendedSearch} extendedSearchData={extSearchData} listingCategoryId={listingCategory.id} /> : <ZeroResults listingCategoryLabel={listingCategory.friendly} />}
          </>}
      </div>
    </div>;
}
export default connect<StateProps, void, OwnProps, RootState>(state => ({
  items: state.items.list,
  loading: state.items.loading,
  hasError: state.items.hasError,
  listingCategory: state.search.category,
  adType: state.search.adType,
  filters: state.search.filters,
  filtersLabels: state.search.filtersLabels,
  query: state.search.query ?? '',
  geo: state.search.geo,
  search: state.search,
  filtersConfig: state.filtersConfig,
  environmentId: state.env.id ?? undefined
}))(ItemListContainer);